// ═══════════════════════════════════════════════════════════════
// Atlas data layer — where the Cognitive Atlas gets its answers from.
//
// Two sources, one shape:
//   1. LOCAL: every finished match appends its real answerLog to
//      localStorage (guests get a device-local Atlas; also an offline cache).
//   2. DB BACKFILL (signed-in): the answers table already stores every
//      per-question result (solo included), linked to the player the same
//      way Profile links history (users.nickname ilike). The Atlas can
//      therefore be charted retroactively from games played before this
//      feature existed.
//
// Rule: if the DB returns events for a signed-in player, DB wins (it is
// the fuller record). Otherwise the local log is used. No merging — the
// two sources overlap and deduping across them is not worth the risk of
// double-counting.
// ═══════════════════════════════════════════════════════════════

import { supabase } from '../supabase';
import { getPlayerId } from './playerId';
import type { AnswerEvent } from './cognitiveEngine';
import { regionOfRealm } from './cognitiveEngine';
import { MAX_CLOCK_MS } from '@wot/shared';

const LOCAL_KEY = 'wot_atlas_events_v1';
const LOCAL_CAP = 2000;   // events kept on device
const DB_CAP = 1500;      // events pulled from backfill

// ── Local accumulation (called once per finished match) ─────────
export function saveMatchToLocalAtlas(log: AnswerEvent[]): void {
  if (log.length === 0) return;
  try {
    const matchDate = new Date().toISOString();
    const stamped = log.map(e => ({ ...e, matchDate }));
    const prev: AnswerEvent[] = JSON.parse(localStorage.getItem(LOCAL_KEY) || '[]');
    const next = [...prev, ...stamped].slice(-LOCAL_CAP);
    localStorage.setItem(LOCAL_KEY, JSON.stringify(next));
  } catch { /* storage full/blocked: the Atlas just stays thinner */ }
}

export function loadLocalAtlasEvents(): AnswerEvent[] {
  try { return JSON.parse(localStorage.getItem(LOCAL_KEY) || '[]'); }
  catch { return []; }
}

// ── DB backfill (same identity path Profile uses) ───────────────
export async function fetchAtlasEventsFromDb(displayNick: string): Promise<AnswerEvent[]> {
  try {
    // Same identity path Profile uses: the player's own row, with the old
    // nickname match kept only for a device that has never played.
    const playerId = await getPlayerId();
    const base = supabase
      .from('match_players')
      .select(`
        id,
        matches!inner ( created_at ),
        users!inner ( nickname ),
        answers (
          is_correct, response_time_ms,
          rounds ( round_number, questions ( realm, difficulty ) )
        )
      `)
      .limit(200);

    const { data, error } = playerId
      ? await base.eq('user_id', playerId)
      : await base.ilike('users.nickname', displayNick);

    if (error || !data) return [];

    const events: AnswerEvent[] = [];
    for (const mp of data as any[]) {
      const matchDate = mp.matches?.created_at;
      for (const a of mp.answers || []) {
        const q = a.rounds?.questions;
        if (!q?.realm || !q?.difficulty) continue;
        events.push({
          realm: String(q.realm).toUpperCase(),
          difficulty: q.difficulty,
          correct: !!a.is_correct,
          responseTimeMs: Math.min(MAX_CLOCK_MS, Math.max(0, a.response_time_ms ?? MAX_CLOCK_MS)),
          roundIndex: a.rounds?.round_number ?? 1,
          matchDate,
        });
      }
    }

    // Elo is sequential: order by match date, then round order within a match.
    events.sort((x, y) => {
      const d = new Date(x.matchDate || 0).getTime() - new Date(y.matchDate || 0).getTime();
      return d !== 0 ? d : x.roundIndex - y.roundIndex;
    });
    return events.slice(-DB_CAP);
  } catch {
    return [];
  }
}

/** Single entry point: DB record for signed-in players, local log otherwise. */
export async function loadAtlasEvents(displayNick: string | null): Promise<AnswerEvent[]> {
  if (displayNick) {
    const db = await fetchAtlasEventsFromDb(displayNick);
    if (db.length > 0) return db;
  }
  return loadLocalAtlasEvents();
}

// ── Stars: one star per finished match, laid out deterministically ──
export interface AtlasStar {
  region: string;
  t: number;          // 0..1 position along the region arc
  rr: number;         // 0..1 radial position within the star band
  brightness: number; // 0.3..1, from match accuracy
}

/** Groups events into matches per region and returns star placements.
 *  Deterministic (golden-ratio scatter) so the sky never reshuffles. */
export function computeStars(events: AnswerEvent[], maxPerRegion = 14): AtlasStar[] {
  const byRegionMatch = new Map<string, Map<string, { c: number; n: number }>>();
  for (const e of events) {
    const region = regionOfRealm(e.realm);
    if (!region) continue;
    const matchKey = e.matchDate || 'live';
    if (!byRegionMatch.has(region)) byRegionMatch.set(region, new Map());
    const m = byRegionMatch.get(region)!;
    const cur = m.get(matchKey) || { c: 0, n: 0 };
    cur.n++;
    if (e.correct) cur.c++;
    m.set(matchKey, cur);
  }

  const stars: AtlasStar[] = [];
  for (const [region, matches] of byRegionMatch) {
    const recent = [...matches.entries()].slice(-maxPerRegion);
    recent.forEach(([, { c, n }], i) => {
      const g = (i * 0.618033988749895) % 1; // golden-ratio low-discrepancy scatter
      stars.push({
        region,
        t: g,
        rr: ((i * 0.381966) + 0.15) % 1,
        brightness: 0.3 + 0.7 * (n > 0 ? c / n : 0),
      });
    });
  }
  return stars;
}
